import { format } from 'date-fns';
import { Pencil, Trash2, Users } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { type Column, DataTable } from '@/components/common/DataTable';
import { Button } from '@/components/ui/button';
import { userConfigStore } from '@/stores';
import type { ConfigDefinitionItem } from '@/types';
import { EditDefinitionDialog } from './EditDefinitionDialog';

export const DefinitionTable = observer(() => {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<ConfigDefinitionItem | null>(
    null,
  );

  const handleDelete = (configKey: string) => {
    setSelectedKey(configKey);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (selectedKey) {
      try {
        await userConfigStore.deleteDefinition(selectedKey);
      } catch (error) {
        console.error('删除失败:', error);
      }
    }
    setSelectedKey(null);
  };

  const handleEdit = (item: ConfigDefinitionItem) => {
    setEditingItem(item);
    setEditDialogOpen(true);
  };

  const columns: Column<ConfigDefinitionItem>[] = [
    {
      id: 'id',
      header: 'ID',
      accessor: (row) => <span className="font-mono text-sm">{row.id}</span>,
    },
    {
      id: 'configKey',
      header: '配置键',
      accessor: (row) => (
        <span className="font-mono text-sm font-medium">{row.configKey}</span>
      ),
    },
    {
      id: 'description',
      header: '描述',
      accessor: (row) => (
        <span className="text-muted-foreground">{row.description || '—'}</span>
      ),
    },
    {
      id: 'jsonValue',
      header: 'JSON 值',
      accessor: (row) => (
        <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded max-w-[200px] truncate block">
          {row.jsonValue}
        </code>
      ),
    },
    {
      id: 'updateAt',
      header: '更新时间',
      accessor: (row) =>
        format(new Date(row.updateAt * 1000), 'yyyy-MM-dd HH:mm:ss'),
    },
    {
      id: 'actions',
      header: '操作',
      accessor: (row) => (
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            title="管理用户"
            onClick={() => userConfigStore.openUserDrawer(row.configKey)}
          >
            <Users className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            title="编辑"
            onClick={() => handleEdit(row)}
          >
            <Pencil className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            title="删除"
            onClick={() => handleDelete(row.configKey)}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <>
      <DataTable
        columns={columns}
        data={userConfigStore.definitions}
        loading={userConfigStore.definitionsLoading}
        emptyMessage="暂无配置定义"
      />

      <ConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="确认删除配置"
        description={`确定要删除配置键 "${selectedKey}" 吗？该操作将同时移除所有用户对此配置的分配，不可恢复。`}
        confirmText="删除"
        cancelText="取消"
        variant="destructive"
        onConfirm={confirmDelete}
      />

      <EditDefinitionDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        item={editingItem}
        onClose={() => setEditingItem(null)}
      />
    </>
  );
});
